import { Form, Formik, FormikProps, useField } from 'formik';
import { useEffect, useRef, useState } from 'react';
import { Container, OverlayTrigger, ProgressBar, Tooltip } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import EditIcon from '../../../assets/Edit.svg';
import PosActivationIcon from '../../../assets/POS-activation.svg';
import QuestionIcon from '../../../assets/icon-question.svg';
import Select, { BooleanishOptions } from '../../../components/Input/Select';
import NavigationFooter from '../../../components/NavigationFooter/NavigationFooter';
import { terminalPlan } from '../../../services/commerce.service';
import { TerminalPlanRequest } from '../../../types/business/request';
import { pagesPaths } from '../../../utils/navigationUtils';
import { getProgressPercentage } from '../../../utils/progressHelper';
import schemas from '../../../utils/schemas';

function CommerceCodes() {
  const navigate = useNavigate();
  const formRef = useRef<FormikProps<any> | null>(null);
  const location = useLocation();
  const localState = location.state;

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (localState?.nadOnboarding === false) {
      navigate(pagesPaths.CommerceDocumentation, {
        state: { ...localState, from: pagesPaths.CommerceConditions },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGoBack = () => navigate(pagesPaths.CommerceTerminals, { state: { ...localState } });

  const handleSubmit = () => formRef.current?.submitForm();

  const submitTerminalPlan = async (values: any) => {
    setIsLoading(true);

    const requestBody: TerminalPlanRequest = {
      id: localState?.terminalPlan?.id,
      commerceCodes: values.commerceCodes,
      allowInstallments: true,
      includesTips: values.includesTips === 'true',
      ivaType: '0', // Default to "Basic" IVA Type
    };

    const response = await terminalPlan(requestBody, localState?.businessId || localState?.id);
    setIsLoading(false);

    if (response.data) {
      return navigate(pagesPaths.CommerceDocumentation, {
        state: { ...localState, ...response.data.businessData, from: pagesPaths.CommerceCodes },
      });
    }
  };

  return (
    <>
      <Container className="commerce-terminals">
        <ProgressBar now={getProgressPercentage('CommerceCodes')} />
        <h1 className="title">
          <img src={PosActivationIcon} alt="" className="me-3" />
          Nueva Terminal Getnet
        </h1>
        <Formik
          initialValues={{
            commerceCodes: {
              amex: localState?.terminalPlan?.amexCode || '',
              anda: localState?.terminalPlan?.andaCode || '',
              cabal: localState?.terminalPlan?.cabalCode || '',
              cabalDolar: localState?.terminalPlan?.cabalDolarCode || '',
              clubDelEste: localState?.terminalPlan?.clubDelEsteCode || '',
              creditel: localState?.terminalPlan?.creditelCode || '',
              diners: localState?.terminalPlan?.dinersCode || '',
              oca: localState?.terminalPlan?.ocaCode || '',
              passcard: localState?.terminalPlan?.passcardCode || '',
              tarjetaD: localState?.terminalPlan?.tarjetaDCode || '',
              cdlcQr: localState?.terminalPlan?.cdlcQrCode || '',
            },
            includesTips: localState?.terminalPlan
              ? localState?.terminalPlan.includesTips
                ? 'true'
                : 'false'
              : '',
          }}
          onSubmit={submitTerminalPlan}
          validationSchema={schemas.CommerceCodesSchema}
          innerRef={(formElement) => (formRef.current = formElement)}
        >
          <Form className="row">
            <h3 className="section-title mt-4">CÓDIGO DE COMERCIO</h3>
            <CommerceCodesTable />

            <h3 className="section-title mt-6">TIPO DE PLAN</h3>
            <Select className="col-md-12" name="includesTips" label="¿El comercio utiliza propina?" required>
              <BooleanishOptions />
            </Select>
          </Form>
        </Formik>
      </Container>
      <NavigationFooter previousHandler={handleGoBack} nextHandler={handleSubmit} loading={isLoading} />
    </>
  );
}

function CommerceCodesTable() {
  return (
    <div className="table-wrapper">
      <div className="table-title codes-table-cols mt-4">
        {['Tarjeta', 'Adquiriente', 'Comercio'].map((heading) => (
          <div key={heading}>{heading}</div>
        ))}
      </div>
      <div className="table-description codes-table-cols">
        <div>Master card</div>
        <div>Adquirencia Getnet</div>
        <div>Código de comercio Getnet</div>
      </div>
      <div className="table-description codes-table-cols">
        <div>VISA</div>
        <div>Adquirencia Getnet</div>
        <div>Código de comercio Getnet</div>
      </div>

      <CommerceCodeRow name="commerceCodes.amex" length={10} productName="AMEX" />
      <CommerceCodeRow name="commerceCodes.anda" length={12} productName="ANDA" />
      <CommerceCodeRow name="commerceCodes.cabal" length={11} productName="CABAL" />
      <CommerceCodeRow name="commerceCodes.cabalDolar" length={11} productName="CABAL DÓLARES" />
      <CommerceCodeRow name="commerceCodes.clubDelEste" length={4} productName="CLUB DEL ESTE" />
      <CommerceCodeRow name="commerceCodes.creditel" length={6} productName="CREDITEL" />
      <CommerceCodeRow name="commerceCodes.diners" length={8} productName="LIDER/DINERS" />
      <CommerceCodeRow name="commerceCodes.oca" length={6} productName="OCA PRODUCTOS" />
      <CommerceCodeRow name="commerceCodes.passcard" length={5} productName="PASSCARD" />
      <CommerceCodeRow name="commerceCodes.tarjetaD" length={4} productName="TARJETA D" />
      <CommerceCodeRow name="commerceCodes.cdlcQr" length={6} productName="CRÉDITO DE LA CASA QR" />
    </div>
  );
}

function CommerceCodeRow({
  name,
  length,
  productName,
}: {
  name: string;
  length: number;
  productName: string;
}) {
  const [field, meta, helpers] = useField(name);

  const handleEdit = (event: React.MouseEvent<HTMLSpanElement>) => {
    const parent = event.currentTarget.parentElement;
    const input = parent?.querySelector('input');

    if (!input) return;

    input.focus();
    helpers.setValue('');
  };

  return (
    <div className="table-description codes-table-cols">
      <div>{productName}</div>
      <div>-</div>
      <div className="d-flex justify-content-center gap-3">
        <div className="d-flex flex-column">
          <input className="codes-edit-input" {...field} />
          <span className="text-error form-text">{meta.error}</span>
        </div>
        <span onClick={handleEdit} className="h-100 justify-content-center align-items-center">
          <img alt="edit" src={EditIcon} />
        </span>
        <QuestionTooltip length={length} />
      </div>
    </div>
  );
}

function QuestionTooltip({ length }: { length?: number }) {
  return (
    <OverlayTrigger
      placement="bottom"
      overlay={<Tooltip>El código de comercio debe tener {!length ? '4 o 5' : length} dígitos.</Tooltip>}
    >
      <span className="h-100 justify-content-center align-items-center">
        <img alt="question" src={QuestionIcon} />
      </span>
    </OverlayTrigger>
  );
}

export default CommerceCodes;
